import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import Pagination from '@/components/feature/Pagination';
import { useSeo } from '@/hooks/useSeo';
import { NEWS_CATEGORIES, newsList } from '@/utils/media';

const PAGE_SIZE = 6;

export default function NewsListPage() {
  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);

  useSeo({
    title: '影视资讯 - 91爱爱影视资讯平台',
    description:
      '91爱爱影视资讯频道，提供国内资讯、国际资讯、影人报道与行业观察，及时了解影视行业动态与市场趋势。',
    keywords: '91爱爱,影视资讯,行业动态,影人报道,国际影讯,影视市场',
  });

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filtered = useMemo(
    () => (category === '全部' ? newsList : newsList.filter((item) => item.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const featured = category === '全部' ? filtered[0] : null;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: '影视资讯' }]} />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="rounded-lg border border-background-200 bg-primary-50/60 p-5 md:p-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            汇总国内外影视行业动态、影人报道与市场观察，帮助你在了解作品之外，读懂行业正在发生什么。
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {NEWS_CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs transition-colors cursor-pointer ${
                category === item
                  ? 'border-primary-500 bg-primary-600 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {featured ? (
          <Link
            to={`/news/detail/${featured.id}`}
            className="group mt-6 grid gap-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:grid-cols-2 cursor-pointer"
          >
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200 md:aspect-auto md:h-full md:min-h-[260px]">
              <img
                src={featured.cover}
                alt={featured.title}
                title={`${featured.title} - 91爱爱资讯`}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                头条
              </span>
            </div>
            <div className="flex flex-col justify-center gap-3 p-6">
              <span className="w-fit rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                {featured.category}
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                {featured.title}
              </h2>
              <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">
                {featured.summary}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {featured.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-newspaper-line" />
                  {featured.source}
                </span>
              </div>
            </div>
          </Link>
        ) : null}

        <div className="mt-8 flex items-center justify-between border-b border-background-200 pb-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-list-unordered text-primary-600" />
            {category === '全部' ? '最新资讯' : `${category}（${filtered.length}）`}
          </h2>
          <span className="text-xs text-foreground-500">共 {filtered.length} 条</span>
        </div>

        {current.length > 0 ? (
          <>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {current.map((article) => (
                <NewsCard key={article.id} article={article} variant="card" />
              ))}
            </div>
            <Pagination page={page} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20">
            <i className="ri-newspaper-line text-5xl text-foreground-300" />
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">
              暂无该分类的资讯内容
            </p>
            <button
              type="button"
              onClick={() => setCategory('全部')}
              className="mt-5 h-10 whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
            >
              查看全部资讯
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}